import { FC, useMemo, useRef, useState } from "react";
import { Form, Formik } from "formik";
import { useParams } from "react-router-dom";
import * as Yup from "yup";
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  Typography,
  useTheme
} from "@mui/material";
import { Button } from "@vilocnv/allsetra-core";
import { useTranslation } from "react-i18next";
import { useAppDispatch } from "hooks";
import {
  getSpecificObjectByIdThunk,
  patchObjectOperationalHoursThunk
} from "app/features";
import {
  OperationalHourCounter,
  OperationalHoursCorrection,
  OperationalHoursRequestError,
  formatOperationalHours,
  isNoOpCorrection
} from "app/data/helpers/operationalHoursHelpers";
import {
  ModalContent,
  ModalHeader,
  SystemMileageField
} from "../MileageCorrectionModal/MileageCorrectionModal.styled";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  counter: OperationalHourCounter;
  counterLabel: string;
  currentValue: number;
  specificObject: { uniqueId?: string };
  accountId: string;
  onCorrected: (correction: OperationalHoursCorrection) => void;
  onHistoryRefresh: () => Promise<void>;
}

interface FormValues {
  newValue: string;
}

const OperationalHoursCorrectionModal: FC<Props> = ({
  isOpen,
  onClose,
  counter,
  counterLabel,
  currentValue,
  specificObject,
  accountId,
  onCorrected,
  onHistoryRefresh
}) => {
  const dispatch = useAppDispatch();
  const params = useParams();
  const { t } = useTranslation(["translation"]);
  const theme = useTheme();
  const [apiError, setApiError] = useState<OperationalHoursRequestError | null>(
    null
  );
  const submittingRef = useRef(false);

  const validationSchema = useMemo(() => {
    return Yup.object().shape({
      newValue: Yup.number()
        .transform((value, originalValue) => {
          if (originalValue === "" || originalValue == null) return undefined;
          if (typeof originalValue === "string") {
            return Number(originalValue.trim().replace(",", "."));
          }
          return value;
        })
        .typeError(t("validation.mustBeNumber"))
        .required(t("validation.operationalHoursRequired"))
        .min(0, t("validation.cannotBeNegative"))
        .test(
          "finite",
          t("validation.mustBeNumber"),
          (value) => value == null || Number.isFinite(value)
        )
    });
  }, [t]);

  const initialValues = useMemo<FormValues>(
    () => ({
      newValue: ""
    }),
    [isOpen, counter]
  );

  const errorMessage = (() => {
    if (!apiError) return null;
    if (apiError.status === 403) {
      return t("errors.operationalHours.forbidden");
    }
    if (apiError.status === 404) {
      return t("errors.operationalHours.notFound");
    }
    return apiError.message;
  })();

  const closeModal = () => {
    setApiError(null);
    onClose();
  };

  const handleModalClose = () => {
    if (submittingRef.current) return;
    closeModal();
  };

  return (
    <Dialog
      open={isOpen}
      onClose={handleModalClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "12px",
          maxHeight: "90vh"
        }
      }}
      sx={{
        width: "460px",
        margin: "0 auto"
      }}
    >
      <ModalHeader>
        <Box display="flex" flexDirection="column" alignItems="center" gap={2}>
          <Box>
            <Typography variant="h5" fontWeight={600} textAlign="center">
              {t("typography.correctOperationalHours", {
                counter: counterLabel
              })}
            </Typography>
          </Box>
          <Box>
            <Typography
              variant="body2"
              color="text.secondary"
              textAlign="center"
            >
              {t("typography.operationalHoursModalDescription", {
                counter: counterLabel
              })}
            </Typography>
          </Box>
        </Box>
      </ModalHeader>

      <DialogContent sx={{ p: 0 }}>
        <ModalContent>
          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={() => undefined}
            enableReinitialize
            validateOnMount
          >
            {({
              isValid,
              isSubmitting,
              values,
              setFieldValue,
              setSubmitting,
              errors,
              touched,
              setFieldTouched
            }) => {
              const handleSave = async () => {
                setApiError(null);
                submittingRef.current = true;
                setSubmitting(true);

                try {
                  const result = await dispatch(
                    patchObjectOperationalHoursThunk({
                      accountId: accountId || "",
                      objectId: specificObject?.uniqueId || "",
                      data: {
                        counter,
                        newValue: Number(
                          String(values.newValue).trim().replace(",", ".")
                        )
                      }
                    })
                  ).unwrap();

                  if (!result || isNoOpCorrection(result)) {
                    submittingRef.current = false;
                    closeModal();
                    return;
                  }

                  onCorrected(result);
                  try {
                    await dispatch(
                      getSpecificObjectByIdThunk({
                        accountId: accountId || "",
                        objectId: params.id || specificObject?.uniqueId || "",
                        silent: true
                      })
                    ).unwrap();
                  } catch {
                    // The correction is already saved. Keep the hours tab open
                    // and still reload the history below.
                  }
                  await onHistoryRefresh();
                  submittingRef.current = false;
                  closeModal();
                } catch (error) {
                  const requestError = error as OperationalHoursRequestError;
                  submittingRef.current = false;
                  setSubmitting(false);
                  setApiError(
                    requestError?.status
                      ? requestError
                      : {
                          status: 0,
                          message: t("errors.operationalHours.failed")
                        }
                  );
                }
              };

              return (
                <Form>
                  <Box
                    sx={{ display: "flex", flexDirection: "column", gap: 3 }}
                  >
                    <SystemMileageField>
                      <Typography variant="body2" fontWeight={500}>
                        {t("typography.operationalHoursCurrentValue")}
                      </Typography>
                      <Typography variant="body2" fontWeight={600}>
                        {t("typography.operationalHoursValue", {
                          value: formatOperationalHours(currentValue)
                        })}
                      </Typography>
                    </SystemMileageField>

                    <Box>
                      <Typography
                        variant="body2"
                        fontWeight={500}
                        sx={{ mb: 1 }}
                      >
                        {t("typography.operationalHoursNewValue")}
                        <Typography
                          component="span"
                          sx={{ color: "error.main", ml: 0.5 }}
                        >
                          *
                        </Typography>
                      </Typography>
                      <input
                        type="text"
                        inputMode="decimal"
                        value={values.newValue}
                        onChange={(event) => {
                          setApiError(null);
                          setFieldValue("newValue", event.target.value);
                        }}
                        onBlur={() => setFieldTouched("newValue", true)}
                        placeholder={t("typography.enterOperationalHours")}
                        aria-label={t("typography.operationalHoursNewValue")}
                        style={{
                          width: "100%",
                          padding: "12px",
                          border: `1px solid ${
                            errors.newValue && touched.newValue
                              ? theme.palette.error.main
                              : theme.palette.divider
                          }`,
                          borderRadius: "8px",
                          fontSize: "14px"
                        }}
                        required
                      />
                      {errors.newValue && touched.newValue && (
                        <Typography
                          variant="caption"
                          color="error"
                          sx={{ mt: 0.5, display: "block" }}
                        >
                          {errors.newValue}
                        </Typography>
                      )}
                    </Box>

                    {errorMessage && (
                      <Typography
                        variant="body2"
                        color="error"
                        data-testid="operational-hours-error"
                      >
                        {errorMessage}
                      </Typography>
                    )}
                  </Box>

                  <DialogActions
                    sx={{
                      paddingY: 3,
                      gap: 2,
                      borderTop: `1px solid ${theme.palette.divider}`,
                      marginTop: 2
                    }}
                  >
                    <Button
                      variant="outlined"
                      onClick={handleModalClose}
                      disabled={isSubmitting}
                    >
                      {t("typography.cancel")}
                    </Button>
                    <Button
                      variant="contained"
                      onClick={handleSave}
                      disabled={!isValid || isSubmitting}
                      loading={isSubmitting}
                      theme={theme}
                    >
                      {t("typography.addCorrection")}
                    </Button>
                  </DialogActions>
                </Form>
              );
            }}
          </Formik>
        </ModalContent>
      </DialogContent>
    </Dialog>
  );
};

export default OperationalHoursCorrectionModal;
